import React, { useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, KeyRound, User, Mail, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { AccountsState } from '../types';
import { loginWithEmail as supabaseLoginWithEmail, loginWithUsername as supabaseLoginWithUsername } from '../../data/supabase/authService';
import { refreshProfile, getActiveProfile } from '../../data/supabase/session';
import { getRoster, rememberProfile, forgetProfile, type RosterProfile } from '../../data/supabase/deviceRoster';
import { ProfilePicker } from './ProfilePicker';
import { RecoverAccountPanel } from './RecoverAccountPanel';
import { DjayNarrator, DjayBubble, BrandLockup } from '../../mascot';
import { useMotionBudget } from '../../app/hooks/useMotionBudget';
import { soundManager } from '../../utils/soundManager';
import { hapticManager } from '../../utils/hapticManager';
/**
 * LoginScreen — Warm, mascot-guided login experience.
 *
 * Two modes: Learner (username + password) and Adult (email + password).
 * Djay guides the user with contextual messages.
 * Smooth tab transitions with animated indicator.
 *
 * 14 juli 2026: de koppelcode-inlogmethode is VERWIJDERD. Een 6-tekens code
 * (~30 bits entropie) is te zwak om toegang te geven tot de gegevens van een
 * kind. Koppelcodes bewijzen nu alleen nog een relatie (ouder ↔ kind), geen
 * identiteit. Zie `docs/project/SUPABASE_AUTH_MIGRATION.md` §3.
 *
 * Augustus 2026: de lokale inlogroute is VERWIJDERD. Er was één ingang
 * (Supabase) en één "vangnet" dat sinds de omzetting onbereikbaar was — dus
 * geen vangnet, maar een tweede antwoord op de vraag wie er inlogt.
 * Ratelimiet, wachtwoordverificatie en sessieduur liggen nu volledig bij de
 * server. De meldingen blijven bewust generiek: onbekende gebruiker en fout
 * wachtwoord geven exact dezelfde tekst, zodat niemand kan aftasten welke
 * accounts bestaan.
 */
interface LoginScreenProps {
  onLoginSuccess: (accountId: string, role?: 'learner' | 'parent' | 'teacher' | 'admin') => void;
  onBack: () => void;
  onNavigateToRegister?: () => void;
  // Er stond hier een `accounts`-prop voor de lokale inlogroute. Die route is
  // weg, en de prop met hem — niet als optionele rest, want een prop die
  // niemand leest suggereert dat dit scherm nog iets met de lokale
  // accountlijst doet.
}
type LoginTab = 'learner' | 'adult';
const TAB_MESSAGES: Record<LoginTab, {
  text: string;
  emotion: 'happy' | 'encouraging';
}> = {
  learner: {
    text: 'Welkom terug! Vul je gegevens in 😊',
    emotion: 'happy'
  },
  adult: {
    text: 'Hallo! Log in met je e-mailadres.',
    emotion: 'encouraging'
  }
};
export function LoginScreen({
  onLoginSuccess,
  onBack,
  onNavigateToRegister
}: LoginScreenProps) {
  const {
    shouldAnimate
  } = useMotionBudget();
  const [activeTab, setActiveTab] = useState<LoginTab>('learner');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [shakeKey, setShakeKey] = useState(0);
  const [showRecovery, setShowRecovery] = useState(false);

  // ── Profielkiezer ──────────────────────────────────────────────────
  // Alleen zichtbaar als iemand het rooster op dit apparaat heeft aangezet.
  // Een tegel logt niet in: hij vult het inlogveld voor. Het wachtwoord blijft
  // altijd vereist — er staan geen sessies of sleutels op dit apparaat.
  const [roster, setRoster] = useState<RosterProfile[]>(() => getRoster());
  const [showPicker, setShowPicker] = useState(roster.length > 0);
  const handlePickProfile = (profile: RosterProfile) => {
    setActiveTab(profile.role === 'learner' ? 'learner' : 'adult');
    setUsername(profile.username ?? '');
    setEmail(profile.email ?? '');
    setPassword('');
    setError('');
    setShowPicker(false);
  };
  const handleForgetProfile = (id: string) => {
    forgetProfile(id);
    const next = getRoster();
    setRoster(next);
    if (next.length === 0) setShowPicker(false);
  };
  const djayMsg = error ? {
    text: 'Hmm, dat klopt niet helemaal. Probeer het nog eens!',
    emotion: 'thinking' as const
  } : TAB_MESSAGES[activeTab];
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (isLoading) return;
    setIsLoading(true);
    try {
      // De rol komt uit `public.profiles`, niet uit localStorage. Dat is wat
      // een admin daadwerkelijk in het adminportaal laat landen.
      //
      // Hieronder stond een tweede, lokale inlogroute als "vangnet". Die was
      // sinds de omzetting onbereikbaar, en een vangnet dat niet kan vangen is
      // geen vangnet maar een tweede antwoord op de vraag wie er inlogt.
      const supaResult = activeTab === 'learner' ? await supabaseLoginWithUsername(username, password) : await supabaseLoginWithEmail(email, password);
      if (supaResult.ok && supaResult.session) {
        // Wacht tot de sessie het profiel kent vóór navigeren, anders draait
        // de routing te vroeg en belandt de gebruiker terug op het
        // welkomstscherm omdat er nog geen actief account is.
        await refreshProfile();
        // Pas NA een geslaagde inlog vastleggen. Zou een mislukte poging al
        // een tegel achterlaten, dan wordt het rooster een manier om te
        // achterhalen welke accounts bestaan.
        const profile = getActiveProfile();
        if (profile) {
          rememberProfile({
            id: profile.id,
            displayName: profile.displayName ?? 'Onbekend',
            role: profile.role,
            username: profile.role === 'learner' ? profile.username : null,
            usernameNamespace: null,
            email: profile.role === 'learner' ? null : profile.email,
            avatar: null
          });
        }
        soundManager.playLevelUp();
        hapticManager.hapticSuccess();
        onLoginSuccess(supaResult.session.accountId, supaResult.session.role);
        return;
      }

      // De Supabase-service normaliseert zijn meldingen al: onbekende
      // gebruiker en fout wachtwoord geven exact dezelfde tekst, zodat je
      // niet kunt aftasten welke accounts bestaan.
      setError(supaResult.message ?? 'Gegevens kloppen niet. Probeer het opnieuw.');
      soundManager.playError();
      hapticManager.hapticError();
      setShakeKey(k => k + 1);
    } finally {
      setIsLoading(false);
    }
  };
  const switchTab = (tab: LoginTab) => {
    soundManager.playClick();
    hapticManager.hapticLight();
    setActiveTab(tab);
    setError('');
    setPassword('');
  };
  return <div className="min-h-screen bg-gradient-to-b from-orange-50 via-amber-50/30 to-white flex items-center justify-center p-4 sm:p-8 relative overflow-hidden" data-id="element-12" data-mp="tpyjrnczq7jd">
      {/* Brand Header (Desktop) */}
      <div className="absolute top-6 left-6 hidden md:block z-20" data-id="element-13" data-mp="2i9kuhr0x6svw">
        <BrandLockup size="sm" data-id="element-14" data-mp="2iavy9qud80n0" />
      </div>

      <div className="w-full max-w-md relative z-10" data-id="element-15" data-mp="2e77qp85qh51r">
        {/* Back button */}
        <motion.button onClick={onBack} className="flex items-center gap-2 text-slate-400 hover:text-slate-700 mb-6 transition-colors font-semibold text-sm" initial={{
        opacity: 0,
        x: -10
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-16" data-mp="29g7ts2wm6mz9">
          
          <ArrowLeft className="w-4 h-4" data-id="element-17" data-mp="1uife9ar2hyl2" /> Terug
        </motion.button>

        {/* Djay guide */}
        <motion.div className="flex flex-col items-center mb-6" initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.4
      }} data-id="element-18" data-mp="1qiwtcez8xni8">
          
          <motion.div animate={{
          y: [0, -6, 0]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-19" data-mp="3kttrwdkgbywx">
            
            <DjayNarrator size="lg" emotion={djayMsg.emotion} animate={true} data-id="element-20" data-mp="2os94iptn4qpj" />
          </motion.div>
          <div className="mt-3 w-full max-w-[240px]" data-id="element-21" data-mp="2tpr2xma17nja">
            <AnimatePresence mode="wait" data-id="element-22" data-mp="2f2fu3s58ihmv">
              <motion.div key={activeTab} initial={{
              opacity: 0,
              y: 5
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -5
            }} transition={{
              duration: 0.2
            }} data-id="element-23" data-mp="2f1e7cqeuw6pb">
                
                <DjayBubble message={djayMsg.text} position="top" autoDismissMs={0} colorScheme="amber" data-id="element-24" data-mp="1w2s5h9bimbkc" />
                
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Card */}
        <motion.div key={shakeKey} className="bg-white rounded-3xl border border-slate-200 shadow-lg overflow-hidden" initial={{
        opacity: 0,
        y: 20
      }} animate={shakeKey > 0 && shouldAnimate ? {
        // `opacity` en `y` MOETEN hier mee. Door `key={shakeKey}`
        // remount deze kaart bij elke foute poging met
        // `initial={{opacity: 0, y: 20}}`. Zette de shake-variant die
        // niet terug, dan bleef de kaart ONZICHTBAAR: alleen Djay's
        // tekstballon was nog te zien en corrigeren was onmogelijk.
        opacity: 1,
        y: 0,
        x: [-10, 10, -10, 10, 0],
        transition: {
          duration: 0.4
        }
      } : {
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        delay: shakeKey > 0 ? 0 : 0.1
      }} data-id="element-25" data-mp="3qhupwslpj3ge">
          
          <div className="p-6 sm:p-8" data-id="element-26" data-mp="2cgpxsvhowpo6">
            {showPicker ? <ProfilePicker profiles={roster} onSelect={handlePickProfile} onSomeoneElse={() => setShowPicker(false)} onForget={handleForgetProfile} data-id="element-27" data-mp="27ieqosis4t9n" /> : <>
            {/* Title */}
            <h1 className="text-2xl font-black text-slate-900 text-center mb-6" data-id="element-28" data-mp="d43yca2z7gz4">
              Inloggen
            </h1>

            {/* Tab switcher with animated indicator */}
            <div className="relative flex p-1 bg-slate-100 rounded-2xl mb-6" data-id="element-29" data-mp="36rqn69h1lt7t">
              <motion.div className="absolute top-1 bottom-1 rounded-xl bg-white shadow-sm" initial={false} animate={{
                left: activeTab === 'learner' ? '4px' : '50%',
                width: 'calc(50% - 4px)'
              }} transition={{
                type: 'spring',
                stiffness: 400,
                damping: 30
              }} data-id="element-30" data-mp="31t5ls6q213hs" />
                
              <button type="button" onClick={() => switchTab('learner')} className={`relative z-10 flex-1 py-3 text-sm font-bold rounded-xl transition-colors ${activeTab === 'learner' ? 'text-orange-600' : 'text-slate-500'}`} data-id="element-31" data-mp="3km9i5zunpug3">
                  
                🎒 Leerling
              </button>
              <button type="button" onClick={() => switchTab('adult')} className={`relative z-10 flex-1 py-3 text-sm font-bold rounded-xl transition-colors ${activeTab === 'adult' ? 'text-orange-600' : 'text-slate-500'}`} data-id="element-32" data-mp="c6gdqjdlo9fe">
                  
                👤 Ouder / Docent
              </button>
            </div>

            {showRecovery ?
            // De tabs blijven staan: ze bepalen welke herstelroute geldt, en
            // een kind dat per ongeluk op de volwassen-tab staat moet dat
            // kunnen zien en corrigeren zonder terug te hoeven.
            <RecoverAccountPanel
            // De key hangt aan de modus, zodat het paneel bij een
            // tabwissel opnieuw opbouwt. Zonder key bleef de ingevulde
            // gebruikersnaam en herstelcode van een kind staan terwijl het
            // formulier al op de volwassen-route stond — dan verstuurt een
            // ouder de gegevens van het vorige formulier.
            key={activeTab} mode={activeTab === 'learner' ? 'learner' : 'adult'} initialIdentifier={activeTab === 'learner' ? username : email} onBack={() => setShowRecovery(false)} data-id="element-33" data-mp="ym6ku11dho8j" /> : <form onSubmit={handleLogin} className="space-y-4" data-id="element-34" data-mp="1mcao27anflz0">
              <AnimatePresence mode="wait" data-id="element-35" data-mp="wn3ucwg772za">
                {activeTab === 'learner' && <motion.div key="learner-form" initial={{
                  opacity: 0,
                  x: -20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} exit={{
                  opacity: 0,
                  x: 20
                }} transition={{
                  duration: 0.25
                }} className="space-y-4" data-id="element-36" data-mp="rokbj4wwi7b5">
                    
                    {/*
                      De koppelcode-tab is verwijderd (14 juli 2026). Een
                      6-tekens code (~30 bits) is te zwak als credential voor
                      toegang tot de gegevens van een kind. Koppelcodes bewijzen
                      nu alleen nog een RELATIE (ouder ↔ kind), geen identiteit.
                      Zie docs/project/SUPABASE_AUTH_MIGRATION.md §3.
                      */}
                    <div className="space-y-4" data-id="element-37" data-mp="3m5mfw2vxk76p">
                      <div data-id="element-38" data-mp="3m6xk23tafgyc">
                        <label htmlFor="login-username" className="block text-sm font-bold text-slate-600 mb-1.5" data-id="element-39" data-mp="21m0krn6u632t">
                          
                          Gebruikersnaam
                        </label>
                        <div className="relative" data-id="element-40" data-mp="1rvpf9w7i7ltt">
                          <Input id="login-username" type="text" required value={username} onChange={e => setUsername(e.target.value)} icon={<User className="w-5 h-5" data-id="element-42" data-mp="1iwaoevnfd98s" />} className="bg-slate-50/80 border-2 border-slate-200 rounded-2xl focus:bg-white focus:border-orange-400 focus:ring-2 focus:ring-orange-100 text-base font-medium py-3.5" placeholder="bijv. djayden" autoComplete="username" data-id="element-41" data-mp="1ruwdl9nejmij" />
                          
                        </div>
                      </div>
                      <div data-id="element-43" data-mp="1dx7vuaomsvhp">
                        <label htmlFor="login-password" className="block text-sm font-bold text-slate-600 mb-1.5" data-id="element-44" data-mp="33bw6a6dnw3d3">
                          
                          Wachtwoord
                        </label>
                        <div className="relative" data-id="element-45" data-mp="188hb79lkp58z">
                          <Input id="login-password" type="password" required value={password} onChange={e => setPassword(e.target.value)} icon={<KeyRound className="w-5 h-5" data-id="element-47" data-mp="137jtsz8e0f8j" />} className="bg-slate-50/80 border-2 border-slate-200 rounded-2xl focus:bg-white focus:border-orange-400 focus:ring-2 focus:ring-orange-100 text-base font-medium py-3.5" placeholder="••••••••" autoComplete="current-password" data-id="element-46" data-mp="1887nxtrwxc6z" />
                          
                        </div>
                      </div>
                    </div>
                  </motion.div>}

                {activeTab === 'adult' && <motion.div key="adult-form" initial={{
                  opacity: 0,
                  x: 20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} exit={{
                  opacity: 0,
                  x: -20
                }} transition={{
                  duration: 0.25
                }} className="space-y-4" data-id="element-48" data-mp="fjahqp0z75ul">
                    
                    <div data-id="element-49" data-mp="2d6l0bew0u81r">
                      <label htmlFor="login-email" className="block text-sm font-bold text-slate-600 mb-1.5" data-id="element-50" data-mp="2d7e9emigd9n2">
                        
                        E-mailadres
                      </label>
                      <div className="relative" data-id="element-51" data-mp="2n6ljn0tw4o5y">
                        <Input id="login-email" type="email" required value={email} onChange={e => setEmail(e.target.value)} icon={<Mail className="w-5 h-5" data-id="element-53" data-mp="1uhxv8wduhio6" />} className="bg-slate-50/80 border-2 border-slate-200 rounded-2xl focus:bg-white focus:border-orange-400 focus:ring-2 focus:ring-orange-100 text-base font-medium py-3.5" placeholder="naam@voorbeeld.nl" autoComplete="email" data-id="element-52" data-mp="2r7xdxjnplxkh" />
                        
                      </div>
                    </div>
                    <div data-id="element-54" data-mp="28anqzsj6e1yh">
                      <label htmlFor="login-password" className="block text-sm font-bold text-slate-600 mb-1.5" data-id="element-55" data-mp="1bqwvw78tbl43">
                        
                        Wachtwoord
                      </label>
                      <div className="relative" data-id="element-56" data-mp="2j2mkpbuj6e1x">
                        <Input id="login-password" type="password" required value={password} onChange={e => setPassword(e.target.value)} icon={<KeyRound className="w-5 h-5" data-id="element-58" data-mp="2e28n6nqk2mi5" />} className="bg-slate-50/80 border-2 border-slate-200 rounded-2xl focus:bg-white focus:border-orange-400 focus:ring-2 focus:ring-orange-100 text-base font-medium py-3.5" placeholder="••••••••" autoComplete="current-password" data-id="element-57" data-mp="2j1bhpe8wtabv" />
                        
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>

              {/* Error message */}
              <AnimatePresence data-id="element-59" data-mp="2961i0x5zhf19">
                {error && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} className="overflow-hidden" data-id="element-60" data-mp="292nc5ey42np2">
                    
                    <div className="p-3 bg-red-50 text-red-700 text-sm font-medium rounded-2xl border border-red-200 flex items-start gap-2" role="alert" aria-live="polite" data-id="element-61" data-mp="3o11c4y74gltu">
                      
                      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-62" data-mp="3e4oghus0nxwg" />
                      <span data-id="element-63" data-mp="3j2q5ewc4irfu">{error}</span>
                    </div>
                  </motion.div>}
              </AnimatePresence>

              <Button type="submit" variant="primary" size="lg" className="w-full" disabled={isLoading} data-id="element-64" data-mp="ao7py9jus9m7">
                  
                {isLoading ? <span className="inline-flex items-center gap-2" data-id="element-65" data-mp="foc4vv544wab">
                    <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" aria-hidden="true" data-id="element-66" data-mp="wli8n6q4so7" />
                    
                    Even geduld…
                  </span> : 'Inloggen'}
              </Button>

              {/*
                   Dit was een doodlopend pad ("vraag je meester of juf" /
                   "vraag een beheerder om een reset"). Voor een solo-kind bestaat
                   die meester niet, en een beheerder kon het ook niet: er was geen
                   herstelroute. Nu wél — voor een kind via zijn herstelcode, voor
                   een volwassene via een herstelmail.
                  */}
              <p className="text-center text-xs text-slate-400 mt-3 font-medium" data-id="element-67" data-mp="2gp0mn9273fsn">
                <button type="button" onClick={() => setShowRecovery(true)} className="font-semibold text-slate-500 hover:text-orange-600 hover:underline transition-colors" data-id="element-68" data-mp="3dere0zf6wmu8">
                    
                  {activeTab === 'learner' ? 'Ik weet mijn wachtwoord niet meer' : 'Wachtwoord vergeten?'}
                </button>
              </p>
            </form>}

            {/* Cross-link to Register */}
            {!showRecovery && onNavigateToRegister && <div className="mt-6 pt-5 border-t border-slate-100 text-center" data-id="element-69" data-mp="1szphlfdtaz8p">
                <p className="text-slate-500 font-medium text-sm" data-id="element-70" data-mp="1nzjumcihm0wy">
                  Nog geen account?{' '}
                  <button onClick={onNavigateToRegister} className="text-orange-600 font-bold hover:text-orange-800 hover:underline transition-colors" data-id="element-71" data-mp="1dwxwypow9xtj">
                    
                    Maak er een aan
                  </button>
                </p>
              </div>}
              </>}
          </div>
        </motion.div>
      </div>
    </div>;
}